import { useMemo, memo } from "react";
import Chart from "react-apexcharts";

const PT_MONTHS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function ptLabel(ts) {
  const d = new Date(ts);
  return `${PT_MONTHS[d.getUTCMonth()]}/${d.getUTCFullYear()}`;
}

function ChartSeriesTemporal({ dadosObservados = [], dadosPrevistos = [] }) {
  const { series, options } = useMemo(() => {
    const splitDate = dadosPrevistos[0]?.date ?? null;
    const splitTs = splitDate ? new Date(splitDate).getTime() : null;

    const series = [
      {
        name: "Dados Observados",
        data: dadosObservados.map((d) => ({ x: new Date(d.date).getTime(), y: +d.value.toFixed(2) })),
      },
      {
        name: "Previsão",
        data: dadosPrevistos.map((d) => ({ x: new Date(d.date).getTime(), y: +d.value.toFixed(2) })),
      },
    ];

    const options = {
      chart: {
        type: "area",
        toolbar: { show: true },
        zoom: { enabled: true },
        animations: { enabled: false },
        background: "transparent",
      },
      dataLabels: { enabled: false },
      stroke: {
        width: [2, 2.5],
        dashArray: [0, 6],
        curve: "smooth",
      },
      colors: ["#3b82f6", "#f59e0b"],
      fill: {
        type: ["gradient", "gradient"],
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      markers: {
        size: 0,
        hover: { size: 5 },
      },
      xaxis: {
        type: "datetime",
        tickAmount: 15,
        labels: {
          datetimeUTC: true,
          rotate: -35,
          rotateAlways: false,
          style: { colors: "var(--text-50)", fontSize: "11px" },
          formatter: (val) => ptLabel(val),
        },
        axisBorder: { color: "rgba(46,51,80,0.5)" },
        axisTicks: { color: "rgba(46,51,80,0.5)" },
      },
      yaxis: {
        title: {
          text: "Incidência / 100k hab.",
          style: { color: "var(--text-50)" },
        },
        labels: {
          style: { colors: "var(--text-50)" },
          formatter: (val) =>
            val != null ? val.toFixed(2).replace(".", ",") : "",
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: "dark",
        x: { formatter: (val) => ptLabel(val) },
        y: {
          formatter: (val) =>
            val != null ? val.toFixed(2).replace(".", ",") : "—",
        },
      },
      legend: { show: false },
      grid: {
        borderColor: "rgba(46,51,80,0.5)",
        strokeDashArray: 3,
      },
      annotations: splitTs
        ? {
            xaxis: [
              {
                x: splitTs,
                borderColor: "rgba(245,158,11,0.5)",
                strokeDashArray: 4,
                borderWidth: 1.5,
                label: {
                  text: "Início da Previsão",
                  style: {
                    color: "#f59e0b",
                    background: "rgba(245,158,11,0.10)",
                    fontSize: "11px",
                    fontWeight: 500,
                  },
                  offsetY: -8,
                },
              },
            ],
          }
        : {},
    };

    return { series, options };
  }, [dadosObservados, dadosPrevistos]);

  if (!dadosObservados.length && !dadosPrevistos.length) {
    return <div style={{ height: 400 }} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <Chart
          options={options}
          series={series}
          type="area"
          height="100%"
          width="100%"
        />
      </div>

      {/* Legenda customizada */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "6px 16px 4px", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-50)" }}>
          <div style={{ width: 28, height: 3, background: "#3b82f6", borderRadius: 2 }} />
          Dados Observados
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-50)" }}>
          <div style={{ width: 28, height: 0, borderTop: "3px dashed #f59e0b" }} />
          Previsão
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--text-50)" }}>
          <div style={{ width: 14, height: 12, background: "rgba(245,158,11,0.18)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 2 }} />
          Zona de Previsão
        </div>
      </div>
    </div>
  );
}

export default memo(ChartSeriesTemporal);
